import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { type Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import { getFavorites } from '../lib/favorites';
import { REFERRAL_KIND, REFERRAL_DRTAG } from '../lib/referral';
import { MEMBERSHIP_KIND, parseMembership } from '../lib/communities';

/** Parsed summary of a BCHNostr tip receipt (kind 9735). */
export interface TipInfo {
  /** True when the tip was a CashToken (FURU, PUSD, …) rather than plain BCH. */
  isToken: boolean;
  /** Token ticker, e.g. "FURU" — only set for token tips. */
  currency?: string;
  /** Token genesis category id — only set for token tips. */
  category?: string;
  /** Token amount in BASE units — only set for token tips. */
  amountTokenBase?: bigint;
  /** BCH amount in satoshis — only set for BCH tips. */
  amountSats?: number;
  /** USD value at tip time, if the sender recorded it. */
  amountUsd?: number;
}

export interface Notification {
  id: string;
  event: Event;
  type: 'reply' | 'reaction' | 'repost' | 'zap' | 'mention' | 'friend_post' | 'favorite_post' | 'referral';
  fromPubkey: string;
  relatedEventId?: string;
  timestamp: number;
  /** Set when this is a BCHNostr tip receipt — carries amount + token name. */
  tip?: TipInfo;
}

// ── Notification Preferences ────────────────────────────────────
// Users can toggle which notification types appear in the "All" view
// and count toward the bell badge. Stored in localStorage.

export type NotifType = Notification['type'];

const NOTIF_PREFS_KEY = 'bchnostr:notif-prefs';

// Well-known, high-traffic relays where mentions/replies reliably land
// (they match the propagation relays in useNostrPublish). Notification
// queries include these so a mention is found even when it wasn't written
// to the user's own read relays — the cause of "no mention notification".
const NOTIF_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.nostr.net',
];

const DEFAULT_PREFS: Record<NotifType, boolean> = {
  reply: true,
  reaction: true,
  repost: true,
  zap: true,
  mention: true,
  friend_post: false, // off by default — this is the noisiest one
  favorite_post: true,
  referral: true,
};

export function getNotifPrefs(): Record<NotifType, boolean> {
  try {
    const raw = localStorage.getItem(NOTIF_PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    // Merge with defaults so new types get a default value
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function setNotifPrefs(prefs: Record<NotifType, boolean>) {
  try {
    localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs));
  } catch {}
}

export function useNotifPrefs() {
  const [prefs, setPrefsState] = useState<Record<NotifType, boolean>>(getNotifPrefs);

  const togglePref = useCallback((type: NotifType) => {
    setPrefsState(prev => {
      const next = { ...prev, [type]: !prev[type] };
      setNotifPrefs(next);
      return next;
    });
  }, []);

  return { prefs, togglePref };
}

/**
 * Parse a BCHNostr tip receipt (kind 9735) into a TipInfo. Reads the tags the
 * tip button writes (`currency`, `token`, `amount_token`, `amount`) and falls
 * back to the JSON content. Returns null for non-tip 9735 events (e.g. plain
 * Lightning zap receipts that don't carry our tags). Read-only parsing — never
 * a trust boundary; the receipt only decorates a notification.
 */
function parseTipReceipt(event: Event): TipInfo | null {
  const tag = (name: string) => event.tags.find(t => t[0] === name)?.[1];

  let content: Record<string, unknown> = {};
  if (event.content) {
    try { content = JSON.parse(event.content) as Record<string, unknown>; } catch { /* non-JSON receipt */ }
  }

  const isTokenTip = event.tags.some(t => t[0] === 't' && t[1] === 'token-tip');
  const currency = tag('currency') || (typeof content.currency === 'string' ? content.currency : undefined);
  const category = tag('token') || (typeof content.category === 'string' ? content.category : undefined);

  if (isTokenTip || (currency && currency.toLowerCase() !== 'bch')) {
    // Token tip (FURU, PUSD, …)
    let amountTokenBase: bigint | undefined;
    const rawAmt = tag('amount_token') ?? (content.amount_token != null ? String(content.amount_token) : undefined);
    if (rawAmt && /^\d+$/.test(rawAmt)) {
      try { amountTokenBase = BigInt(rawAmt); } catch { /* ignore */ }
    }
    return {
      isToken: true,
      currency: currency || 'token',
      category,
      amountTokenBase,
      amountUsd: typeof content.amount_usd === 'number' ? content.amount_usd : undefined,
    };
  }

  // BCH tip — only treat as a tip if it carries our amount/tag signature.
  const isBchTip = event.tags.some(t => t[0] === 't' && t[1] === 'bch-tip');
  const rawSats = tag('amount') ?? (content.amount_sats != null ? String(content.amount_sats) : undefined);
  if (!isBchTip && rawSats == null) return null;
  const amountSats = rawSats != null && /^\d+$/.test(rawSats) ? Number(rawSats) : undefined;
  return {
    isToken: false,
    amountSats,
    amountUsd: typeof content.amount_usd === 'number' ? content.amount_usd : undefined,
  };
}

function classifyNotification(event: Event, myPubkey: string): Notification | null {
  if (event.pubkey === myPubkey) return null; // skip own events

  let type: Notification['type'] = 'mention';
  let relatedEventId: string | undefined;
  let tip: TipInfo | undefined;

  if (event.kind === 7) {
    type = 'reaction';
    relatedEventId = event.tags.find(t => t[0] === 'e')?.[1];
  } else if (event.kind === 6) {
    type = 'repost';
    relatedEventId = event.tags.find(t => t[0] === 'e')?.[1];
  } else if (event.kind === 9735) {
    type = 'zap';
    relatedEventId = event.tags.find(t => t[0] === 'e')?.[1];
    tip = parseTipReceipt(event) ?? undefined;
  } else if (event.kind === 1 || event.kind === 1111) {
    // kind 1     — regular note reply / mention
    // kind 1111  — NIP-22 comment (community posts & their replies)
    // Both are treated the same: if they reference another event they're a
    // reply, otherwise they're a mention. Community comments are kind 1111,
    // so without this branch they never produced a notification at all.
    const replyTag = event.tags.find(t => t[0] === 'e' || t[0] === 'E');
    if (replyTag) {
      type = 'reply';
      relatedEventId = replyTag[1];
    } else {
      type = 'mention';
    }
  } else {
    return null;
  }

  return {
    id: event.id,
    event,
    type,
    fromPubkey: event.pubkey,
    relatedEventId,
    timestamp: event.created_at,
    tip,
  };
}

export function useNotifications(pubkey: string | undefined, follows?: string[]) {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 3);

  const { events, loading } = useNostrQuery({
    filters: pubkey ? [
      // kind 1111 is included so community comments (NIP-22) generate
      // reply notifications — previously only kind 1 replies did, which
      // meant comments inside Communities were never notified.
      { kinds: [1, 6, 7, 9735, 1111], '#p': [pubkey], since, limit: 120 },
    ] : [],
    enabled: !!pubkey,
    // Always consult the well-known relays (plus the user's own) so mentions
    // and replies are found regardless of where the author published them.
    extraRelays: NOTIF_RELAYS,
  });

  // ── Friend posts: recent kind 1 posts from people you follow ───
  const [friendSince] = useState(() => Math.floor(Date.now() / 1000) - 86400);
  const friendFilter = useMemo(() => {
    if (!follows || follows.length === 0 || !pubkey) return [];
    const capped = follows.slice(0, 100);
    return [{ kinds: [1], authors: capped, limit: 30, since: friendSince }];
  }, [follows, pubkey, friendSince]);

  const { events: friendEvents } = useNostrQuery({
    filters: friendFilter,
    enabled: friendFilter.length > 0,
  });

  // ── Favorites: posts from starred users (last 3 days, higher priority) ─
  const [favorites, setFavorites] = useState<string[]>(() => getFavorites(pubkey));

  // Re-read favorites when they change (star toggled on a profile)
  useEffect(() => {
    const handler = () => setFavorites(getFavorites(pubkey));
    window.addEventListener('bchnostr:favorites-changed', handler);
    return () => window.removeEventListener('bchnostr:favorites-changed', handler);
  }, [pubkey]);

  const favFilter = useMemo(() => {
    if (favorites.length === 0 || !pubkey) return [];
    return [{ kinds: [1], authors: favorites, limit: 50, since: friendSince }];
  }, [favorites, pubkey, friendSince]);

  const { events: favEvents } = useNostrQuery({
    filters: favFilter,
    enabled: favFilter.length > 0,
  });

  const favSet = useMemo(() => new Set(favorites), [favorites]);

  // ── Referral notifications: kind 30078 events with ref tag = my pubkey ─
  // These fire when someone signs up using your referral link.
  const { events: referralEvents } = useNostrQuery({
    filters: pubkey ? [
      { kinds: [REFERRAL_KIND], '#d': [REFERRAL_DRTAG], '#ref': [pubkey], since, limit: 50 },
    ] : [],
    enabled: !!pubkey,
  });

  const [lastReadTime, setLastReadTime] = useState(() => {
    try {
      return parseInt(localStorage.getItem('bchnostr:notif-read-at') || '0', 10);
    } catch { return 0; }
  });

  // Keep lastReadTime in sync across all hook instances in the same tab and across tabs
  useEffect(() => {
    const handleCustom = (e: Event) => {
      setLastReadTime((e as CustomEvent<number>).detail);
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'bchnostr:notif-read-at' && e.newValue) {
        setLastReadTime(parseInt(e.newValue, 10));
      }
    };
    window.addEventListener('bchnostr:notif-read', handleCustom);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('bchnostr:notif-read', handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  /** Helper: is event a top-level post (not a reply, not a game event)? */
  const isTopLevelPost = useCallback((ev: Event, myPubkey: string): boolean => {
    if (ev.pubkey === myPubkey) return false;
    const hasReplyETag = ev.tags?.some((t: string[]) => {
      if (t[0] !== 'e') return false;
      const marker = t[3];
      if (marker === 'mention' || marker === 'edit') return false;
      return true;
    });
    if (hasReplyETag) return false;
    if (ev.tags?.some((t: string[]) => t[0] === 't' && (t[1] === 'bchnostr-chess' || t[1] === 'bchnostr-poker'))) return false;
    return true;
  }, []);

  const notifications: Notification[] = useMemo(() => {
    if (!pubkey) return [];
    const seen = new Set<string>();

    // Regular notifications (p-tagged to me)
    const regular = events
      .map(e => classifyNotification(e, pubkey))
      .filter((n): n is Notification => n !== null);
    regular.forEach(n => seen.add(n.id));

    // Favorite post notifications — highest priority, from starred users
    const favPosts: Notification[] = [];
    for (const ev of favEvents) {
      if (seen.has(ev.id)) continue;
      if (!isTopLevelPost(ev, pubkey)) continue;
      seen.add(ev.id);
      favPosts.push({
        id: ev.id,
        event: ev,
        type: 'favorite_post',
        fromPubkey: ev.pubkey,
        timestamp: ev.created_at,
      });
    }

    // Friend post notifications — from follows (excluding favorites, already handled)
    const friendPosts: Notification[] = [];
    for (const ev of friendEvents) {
      if (seen.has(ev.id)) continue;
      if (!isTopLevelPost(ev, pubkey)) continue;
      // If this author is in favorites, they're already in favPosts — skip
      if (favSet.has(ev.pubkey)) continue;
      seen.add(ev.id);
      friendPosts.push({
        id: ev.id,
        event: ev,
        type: 'friend_post',
        fromPubkey: ev.pubkey,
        timestamp: ev.created_at,
      });
    }

    // Referral notifications — someone signed up using your link
    const referralNotifs: Notification[] = [];
    for (const ev of referralEvents) {
      if (seen.has(ev.id)) continue;
      if (ev.pubkey === pubkey) continue; // skip own events
      seen.add(ev.id);
      referralNotifs.push({
        id: ev.id,
        event: ev,
        type: 'referral',
        fromPubkey: ev.pubkey,
        timestamp: ev.created_at,
      });
    }

    return [...regular, ...favPosts, ...friendPosts, ...referralNotifs]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 80);
  }, [events, friendEvents, favEvents, favSet, referralEvents, pubkey, isTopLevelPost]);

  // Unread count — respects user's notification preferences.
  // Types toggled OFF won't count toward the bell badge.
  const currentPrefs = getNotifPrefs();
  const unreadCount = notifications
    .filter(n => n.timestamp > lastReadTime && currentPrefs[n.type] !== false)
    .length;

  const markAllRead = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    setLastReadTime(now);
    localStorage.setItem('bchnostr:notif-read-at', now.toString());
    // Notify other hook instances in the same tab
    window.dispatchEvent(new CustomEvent('bchnostr:notif-read', { detail: now }));
  }, []);

  return { notifications, unreadCount, markAllRead, lastReadTime, loading };
}

// ── Unread Community Replies counter ──────────────────────────────
// Counts kind 1111 (NIP-22 community post) replies p-tagged to the user.
// Shows a red badge on the Community Spaces nav item.
export function useUnreadCommunityReplies(pubkey: string | undefined) {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 3);

  const { events: replyEvents } = useNostrQuery({
    filters: pubkey ? [
      { kinds: [1111], '#p': [pubkey], since, limit: 50 },
    ] : [],
    enabled: !!pubkey,
  });

  const [lastReadTime, setLastReadTime] = useState(() => {
    try {
      return parseInt(localStorage.getItem('bchnostr:community-read-at') || '0', 10);
    } catch { return 0; }
  });

  const unreadReplies = pubkey
    ? replyEvents.filter(e => e.pubkey !== pubkey && e.created_at > lastReadTime).length
    : 0;

  const markCommunityRead = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    setLastReadTime(now);
    localStorage.setItem('bchnostr:community-read-at', now.toString());
  }, []);

  return { unreadReplies, markCommunityRead };
}

// ── Unread activity in the user's own communities ─────────────────
//
// Unlike useUnreadCommunityReplies (which only counts replies p-tagged
// to YOU), this counts new posts and chat messages across every
// Community Space the user is actually a MEMBER of — so a member gets a
// heads-up when their groups are active, not just when someone replies
// to them directly.
//
// Scope is derived from the user's own kind-30078 membership events
// (self-published + admin-granted), so the badge is inherently limited
// to their groups and never a general firehose.
export interface CommunityActivity {
  /** Total unread posts + chat messages across the user's communities. */
  unreadCount: number;
  /** Per-community unread counts, keyed by the `34550:pubkey:d` coordinate. */
  perCommunity: Record<string, number>;
  /** Community coordinates the user belongs to. */
  communityIds: string[];
  markRead: () => void;
}

const COMMUNITY_ACTIVITY_READ_KEY = 'bchnostr:community-activity-read-at';
const COMMUNITY_POST_KIND = 1111;
const COMMUNITY_CHAT_KIND = 42;

export function useMyCommunityActivity(pubkey: string | undefined): CommunityActivity {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 7);

  // 1. Which communities does this user belong to?
  //    Self-published memberships (authors = me) + admin-granted (#p = me).
  const { events: membershipEvents } = useNostrQuery({
    filters: pubkey ? [
      { kinds: [MEMBERSHIP_KIND], authors: [pubkey], '#t': ['bch-community-member'], limit: 200 },
      { kinds: [MEMBERSHIP_KIND], '#p': [pubkey], '#t': ['bch-community-grant'], limit: 200 },
    ] : [],
    enabled: !!pubkey,
  });

  const communityIds = useMemo(() => {
    if (!pubkey) return [];
    const ids = new Set<string>();
    for (const ev of membershipEvents) {
      const m = parseMembership(ev);
      // Only count memberships that actually belong to THIS user (a granted
      // membership names its target in the d-tag / content).
      if (m && m.pubkey === pubkey && m.communityId) ids.add(m.communityId);
    }
    return [...ids];
  }, [membershipEvents, pubkey]);

  // 2. Recent posts + chat in exactly those communities.
  //    Capped at 60 ids so the filter stays relay-friendly.
  const cappedIds = useMemo(() => communityIds.slice(0, 60), [communityIds]);
  const { events: activityEvents } = useNostrQuery({
    filters: cappedIds.length > 0 ? [
      { kinds: [COMMUNITY_POST_KIND], '#a': cappedIds, since, limit: 300 },
      { kinds: [COMMUNITY_POST_KIND], '#A': cappedIds, since, limit: 300 },
      { kinds: [COMMUNITY_CHAT_KIND], '#a': cappedIds, since, limit: 300 },
    ] : [],
    enabled: cappedIds.length > 0,
  });

  const [lastReadTime, setLastReadTime] = useState(() => {
    try { return parseInt(localStorage.getItem(COMMUNITY_ACTIVITY_READ_KEY) || '0', 10); }
    catch { return 0; }
  });

  // Keep in sync across tabs / hook instances.
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === COMMUNITY_ACTIVITY_READ_KEY && e.newValue) {
        setLastReadTime(parseInt(e.newValue, 10));
      }
    };
    const handleCustom = (e: Event) => {
      setLastReadTime((e as unknown as CustomEvent<number>).detail);
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('bchnostr:community-activity-read', handleCustom as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('bchnostr:community-activity-read', handleCustom as EventListener);
    };
  }, []);

  const { unreadCount, perCommunity } = useMemo(() => {
    const per: Record<string, number> = {};
    let total = 0;
    if (!pubkey) return { unreadCount: 0, perCommunity: per };

    for (const ev of activityEvents) {
      if (ev.pubkey === pubkey) continue;            // don't notify me about my own posts
      if (ev.created_at <= lastReadTime) continue;   // already seen
      // Which community coordinate does this event belong to?
      const coord = ev.tags?.find((t: string[]) => t[0] === 'a' || t[0] === 'A')?.[1];
      if (!coord || !communityIds.includes(coord)) continue;
      per[coord] = (per[coord] || 0) + 1;
      total++;
    }
    return { unreadCount: total, perCommunity: per };
  }, [activityEvents, lastReadTime, pubkey, communityIds]);

  const markRead = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    setLastReadTime(now);
    try { localStorage.setItem(COMMUNITY_ACTIVITY_READ_KEY, now.toString()); } catch {}
    window.dispatchEvent(new CustomEvent('bchnostr:community-activity-read', { detail: now }));
  }, []);

  return { unreadCount, perCommunity, communityIds, markRead };
}

// ── Unread Wallet Tips counter ──────────────────────────────────
// Counts kind 9735 tip receipts p-tagged to the user since they
// last viewed the wallet. Shows a red badge on the Wallet nav icon.
export function useUnreadWalletTips(pubkey: string | undefined) {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 3);

  const { events: tipEvents } = useNostrQuery({
    filters: pubkey ? [
      { kinds: [9735], '#p': [pubkey], '#t': ['bch-tip'], since, limit: 50 },
    ] : [],
    enabled: !!pubkey,
  });

  const [lastWalletViewTime, setLastWalletViewTime] = useState(() => {
    try {
      return parseInt(localStorage.getItem('bchnostr:wallet-viewed-at') || '0', 10);
    } catch { return 0; }
  });

  // Keep in sync across tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'bchnostr:wallet-viewed-at' && e.newValue) {
        setLastWalletViewTime(parseInt(e.newValue, 10));
      }
    };
    const handleCustom = (e: Event) => {
      setLastWalletViewTime((e as CustomEvent<number>).detail);
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('bchnostr:wallet-viewed', handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('bchnostr:wallet-viewed', handleCustom);
    };
  }, []);

  const unreadTips = pubkey
    ? tipEvents.filter(e => e.pubkey !== pubkey && e.created_at > lastWalletViewTime).length
    : 0;

  const markWalletViewed = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    setLastWalletViewTime(now);
    localStorage.setItem('bchnostr:wallet-viewed-at', now.toString());
    window.dispatchEvent(new CustomEvent('bchnostr:wallet-viewed', { detail: now }));
  }, []);

  return { unreadTips, markWalletViewed };
}

// ── Unread DMs counter ────────────────────────────────────────────
export function useUnreadDMs(pubkey: string | undefined) {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 3);

  const { events: dmEvents } = useNostrQuery({
    filters: pubkey ? [
      { kinds: [4], '#p': [pubkey], since, limit: 50 },
    ] : [],
    enabled: !!pubkey,
  });

  const [lastDMReadTime, setLastDMReadTime] = useState(() => {
    try {
      return parseInt(localStorage.getItem('bchnostr:dm-read-at') || '0', 10);
    } catch { return 0; }
  });

  // Only count DMs from others (not own sent messages)
  const unreadDMs = pubkey
    ? dmEvents.filter(e => e.pubkey !== pubkey && e.created_at > lastDMReadTime).length
    : 0;

  const markDMsRead = useCallback(() => {
    const now = Math.floor(Date.now() / 1000);
    setLastDMReadTime(now);
    localStorage.setItem('bchnostr:dm-read-at', now.toString());
  }, []);

  return { unreadDMs, markDMsRead };
}

